// =============================================================================
// ui/tabs/AccountEditor.tsx — Edit Account, the identity-and-rules slice.
// -----------------------------------------------------------------------------
// The old client's modal is FIVE forms in one. This is the one whose worst case
// is confined to a single account row. The other four are named at the foot of
// the modal rather than left as missing sections, because someone arriving from
// the shipping client will look for them:
//
//   ADJUSTMENTS                          a different table, and a Reset fee row
//                                        moves the compliance WINDOW
//   Recompute MAE/MFE                    metered market data, and it overwrites
//                                        excursion fields on hundreds of trades
//
// The firm's COMMISSION table is now here, at the foot, with its own Save — it
// is the one block that reaches past this account, and folding it into the
// modal's Save would mean correcting a nickname also commits a half-typed rate.
// "Apply to existing trades" is deliberately absent and says why on screen:
// this client derives the fee on read, so a saved rate is already in every
// figure and there is nothing to rewrite.
//
// ── ⚠ FOUR RULES HAVE NO FIELD HERE EITHER, AND THAT IS PARITY ──
//
// Lock mechanic, lock pad, daily-limit enforcement and consistency scope are set
// only by applying a saved template in the old client. Templates are not ported,
// so this form cannot change them — and it says so on screen, because the lock
// mechanic decides whether the drawdown floor freezes and a trader has a right
// to know it is not editable here. `mergeAccountRules` carries all four.
// =============================================================================

import { useMemo, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { useSupabase } from '../state/supabase';
import { saveAccount } from '../../io/sync/saveAccount';
import {
  ACCOUNT_TYPE_OPTIONS, MAX_DD_TYPE_OPTIONS, STATUS_OPTIONS,
  applyTemplateToDraft, blockingErrors, draftFromAccount, draftIsDirty,
  draftIsValid, validateAccountDraft,
  type AccountDraft, type DraftErrors,
} from '../../selectors/accountForm';
import {
  parsePropTemplates, templateAge, type PropTemplate,
} from '../../selectors/accountSetup';
import { usePreferences } from '../state/preferences';
// ⚠ The clock is INJECTED, never read from Date.now() (D26) — same source the
// setup modal's picker uses, so the two never disagree about a template's age.
import { useRange } from '../state/range';
import { moneyPlain } from '../format';
import type { Account } from '../../selectors/accounts';
import { CommissionTable } from './CommissionTable';

export interface AccountEditorProps {
  account: Account;
  onCancel: () => void;
  /** Called with the saved draft once the write lands, so the tab can reload. */
  onSaved: () => void;
}

/** Today, by the wall clock, as an ISO date. */
const todayIso = (d: Date): string =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

export function AccountEditor({ account, onCancel, onSaved }: AccountEditorProps) {
  const { sb } = useSupabase();
  const { prefs } = usePreferences();
  const { today } = useRange();
  const [draft, setDraft] = useState<AccountDraft>(() => draftFromAccount(account));
  const [busy, setBusy] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const templates = useMemo<PropTemplate[]>(
    () => parsePropTemplates(prefs?.propTemplates ?? []), [prefs],
  );
  const [tplId, setTplId] = useState<string>('');
  /** What the last Apply did, so the user can see it happened. */
  const [applied, setApplied] = useState<string | null>(null);

  const onApplyTemplate = () => {
    const tpl = templates.find((t) => t.id === tplId) ?? templates[0];
    if (!tpl) return;
    const r = applyTemplateToDraft(draft, tpl);
    setDraft(r.draft);
    // ⚠ NAMES THE SIZE CHANGE SEPARATELY. It moves the drawdown baseline every
    // gauge on the Accounts tab is measured against — too big to slip into a
    // generic "applied" message.
    setApplied(
      r.sizeChangedTo != null
        ? `Applied “${tpl.label}”. Account size set to ${moneyPlain(r.sizeChangedTo, 0)}. Nothing is saved until you press Save.`
        : `Applied “${tpl.label}”. Nothing is saved until you press Save.`,
    );
  };

  const errors = useMemo(() => validateAccountDraft(draft), [draft]);
  const blocking = useMemo(() => blockingErrors(errors), [errors]);
  const canSave = draftIsValid(blocking) && !busy;
  const dirty = draftIsDirty(account, draft);

  const set = <K extends keyof AccountDraft>(k: K, v: AccountDraft[K]) =>
    setDraft((d) => ({ ...d, [k]: v }));

  async function onSave() {
    setBusy(true);
    setSaveError(null);
    // ⚠ `today` is what dates a newly logged fee, and it is what makes a MONTHLY
    // fee amend this month's row rather than reach back and rewrite last
    // month's. See `planFeeAdjustment`.
    const res = await saveAccount(sb, account, draft, { today: todayIso(new Date()) });
    setBusy(false);
    if (!res.ok) { setSaveError(res.error ?? 'The save failed.'); return; }
    // 🔴 THE ACCOUNT SAVED AND THE FEE ROW DID NOT. Not a failed save — sending
    // the trader back to redo an edit that landed would be wrong — but it cannot
    // be silent either, because the cost is what Economics reports an ROI
    // against. Shown here, and the modal stays open so it is read.
    if (res.feeError) {
      setSaveError(
        `Saved — but the account's fee could not be written to the ledger, so the `
        + `Economics report will not count it. ${res.feeError}`,
      );
      return;
    }
    onSaved();
  }

  // ⚠ A PORTAL, like every other modal here. Rendered inside the card it was
  // opened from, the account grid's `overflow: hidden` clips it and the card's
  // `filter: saturate()` on a terminal account creates a containing block that
  // breaks `position: fixed` outright.
  return createPortal(
    <div className="modal-scrim" role="dialog" aria-modal="true" aria-label={`Edit ${account.name}`}>
      <div className="modal wide">
        <header className="modal-head">
          <b>Edit Account</b>
          <button type="button" className="btn" onClick={onCancel} disabled={busy}>×</button>
        </header>

        <div className="modal-body ae-body">
          <div className="ae-banner">
            These rules drive your drawdown gauges and breach warnings.{' '}
            <b>Always verify them against your firm’s own page</b> — firms change
            rules, and we deliberately do not ship copies that can go stale.
          </div>

          <div className="ae-grid">
            <Field label="Account name" error={errors.name}>
              <input value={draft.name} onChange={(e) => set('name', e.target.value)} />
            </Field>
            <Field label="Firm">
              <input value={draft.firm} onChange={(e) => set('firm', e.target.value)}
                placeholder="Apex / Topstep / Personal" />
            </Field>

            <Field label="Account size" hint="the firm’s plan label, e.g. “50k”" error={errors.accountSize}>
              <input inputMode="decimal" value={draft.accountSize}
                onChange={(e) => set('accountSize', e.target.value)} />
            </Field>
            <Field label="Type">
              <select value={draft.accountType}
                onChange={(e) => set('accountType', e.target.value as AccountDraft['accountType'])}>
                {ACCOUNT_TYPE_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </Field>

            <Field label="Starting balance"
              hint="actual $ at day 1; may differ from size if carry-in"
              error={errors.startingBalance}>
              <input inputMode="decimal" value={draft.startingBalance}
                onChange={(e) => set('startingBalance', e.target.value)} />
            </Field>
            {/* ⚠ DISABLED WHILE AUTO-CALC IS ON, and the save does not send it
                either. The stored figure is a cache nothing reads in that mode —
                on Josh's Sim1 it is the float artefact 50226.25000000001, and
                writing it back would persist a rendering bug as data. */}
            <Field
              label="Current balance"
              hint={draft.autoBalance ? 'worked out from your trades — untick below to type one' : ''}
              error={draft.autoBalance ? undefined : errors.currentBalance}
            >
              <input
                inputMode="decimal"
                value={draft.autoBalance ? String(account.currentBalance) : draft.currentBalance}
                disabled={draft.autoBalance}
                onChange={(e) => set('currentBalance', e.target.value)}
              />
            </Field>

            <Check label="Auto-calc balance" note="start + trade P&L + adjustments"
              checked={draft.autoBalance} onChange={(v) => set('autoBalance', v)} />
            <Check label="Exclude from metrics" note="keep out of combined KPIs & analytics"
              checked={draft.excludeFromMetrics} onChange={(v) => set('excludeFromMetrics', v)} />

            <Field full label="Initial peak balance"
              hint="carry-in — leave blank if all trades are logged here"
              error={errors.initialPeakBalance}>
              <input inputMode="decimal" value={draft.initialPeakBalance}
                placeholder="Blank = worked out from your trades"
                onChange={(e) => set('initialPeakBalance', e.target.value)} />
              <p className="ae-help">
                The highest balance this account has <b>ever</b> reached. Only set
                it when you are adding an account you were already trading — it
                gives the trailing-drawdown floor the right starting point. Leave
                it blank if the full history is logged here.
              </p>
            </Field>

            <Field label="Status">
              <select value={draft.status}
                onChange={(e) => set('status', e.target.value as AccountDraft['status'])}>
                {STATUS_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </Field>
            <Field label="Broker account ID">
              <input value={draft.externalId} onChange={(e) => set('externalId', e.target.value)} />
              <p className="ae-help">
                Used to route imported trades to this account. Case-insensitive.
              </p>
            </Field>

            {/* ── 🔴 THE FIELD WHOSE ABSENCE WAS AN UNCLEARABLE PROMPT (#73) ──

                A card with no recorded cost shows "N fields missing — Finish
                setup →". That link opens THIS form, which had no fee input, so
                the prompt could never be cleared. The fee was capturable only
                when the account was first created.

                ⚠ AND IT WRITES THE LEDGER ROW, not just the number. Economics
                reads `account_adjustments`; a form that set `feeAmount` and
                stopped would clear the nudge while the cost never reached the
                report — a confident ROI against a cost nobody recorded. See
                `domain/accountFee.ts`, including why the row is AMENDED. */}
            <Field full label="Account cost"
              hint="real money out — this is the Economics report's expense side">
              <div className="ae-fee">
                <input
                  inputMode="decimal"
                  value={draft.feeAmount}
                  disabled={draft.feeCadence === 'free'}
                  placeholder="e.g. 165"
                  onChange={(e) => set('feeAmount', e.target.value)}
                />
                <div className="acs-seg" role="group" aria-label="Fee cadence">
                  {(['once', 'monthly', 'free'] as const).map((c) => (
                    <button type="button" key={c}
                      className={draft.feeCadence === c ? 'on' : ''}
                      onClick={() => set('feeCadence', c)}>
                      {c === 'once' ? 'One-time' : c === 'monthly' ? 'Monthly' : 'Free'}
                    </button>
                  ))}
                </div>
              </div>
              <p className="ae-help">
                {/* ⚠ "Free" is a WAIVER, not zero. A comped account cost nothing
                    by agreement; a blank one may simply never have been filled
                    in, and collapsing the two flatters cost-per-funded. */}
                Free is for a comped account — it still counts as an attempt and
                contributes $0 to spend. Changing the amount corrects the logged
                fee rather than adding a second one.
              </p>
            </Field>
          </div>

          <h4 className="ae-sec">Drawdown rules</h4>
          <div className="ae-grid">
            <Field label="Max DD type">
              <select value={draft.maxDDType} onChange={(e) => set('maxDDType', e.target.value)}>
                {MAX_DD_TYPE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </Field>
            <Field label="Max DD $" error={errors.maxDDValue}>
              <input inputMode="decimal" value={draft.maxDDValue}
                onChange={(e) => set('maxDDValue', e.target.value)} />
            </Field>
            <Field full label="Daily loss limit $"
              hint="a positive number — most firms set this separately from max DD"
              error={errors.dailyLossLimit}>
              <input inputMode="decimal" value={draft.dailyLossLimit}
                onChange={(e) => set('dailyLossLimit', e.target.value)} />
              <p className="ae-help">
                Drives the “Today’s Loss Room” gauge. Leave it at 0 if this firm
                has no per-day cap.
              </p>
            </Field>
          </div>

          <h4 className="ae-sec">Profit &amp; payout</h4>
          <div className="ae-grid">
            <Field label="Profit target $" error={errors.profitTarget}>
              <input inputMode="decimal" value={draft.profitTarget}
                onChange={(e) => set('profitTarget', e.target.value)} />
            </Field>
            <Field label="Consistency rule %" error={errors.consistencyRulePct}>
              <input inputMode="decimal" value={draft.consistencyRulePct}
                onChange={(e) => set('consistencyRulePct', e.target.value)} />
            </Field>
            <Field label="Min trading days" error={errors.minTradingDays}>
              <input inputMode="decimal" value={draft.minTradingDays}
                onChange={(e) => set('minTradingDays', e.target.value)} />
            </Field>
            <Field label="Payouts taken" error={errors.payoutsTaken}>
              <input inputMode="decimal" value={draft.payoutsTaken}
                onChange={(e) => set('payoutsTaken', e.target.value)} />
            </Field>
            <Field full label="Payout schedule">
              <input value={draft.payoutSchedule}
                placeholder="8 days held, 50/50 split, 14-day cooldown"
                onChange={(e) => set('payoutSchedule', e.target.value)} />
            </Field>
          </div>

          <h4 className="ae-sec">Position limits</h4>
          <div className="ae-grid">
            <Field label="Max contracts" error={errors.maxContracts}>
              <input inputMode="decimal" value={draft.maxContracts}
                onChange={(e) => set('maxContracts', e.target.value)} />
            </Field>
            <Field label="Flat by end of day">
              <select value={draft.eodFlatRule ? 'true' : 'false'}
                onChange={(e) => set('eodFlatRule', e.target.value === 'true')}>
                <option value="false">No</option>
                <option value="true">Yes</option>
              </select>
            </Field>
            <Field full label="Scaling rules">
              <input value={draft.scalingRules}
                placeholder="Half-size before half-target on Apex"
                onChange={(e) => set('scalingRules', e.target.value)} />
            </Field>
            <Field full label="Prohibited products" hint="comma-separated">
              <input value={draft.prohibitedProducts} placeholder="6J, NG, ZB"
                onChange={(e) => set('prohibitedProducts', e.target.value)} />
            </Field>
          </div>

          {/* ── 🚨 THE ONLY WAY TO SET FOUR OF THIS ACCOUNT'S RULES ──────────
              Lock mechanic, lock pad, daily-limit enforcement and consistency
              scope have no input anywhere in either client. Applying a saved
              template is how they are written — which is why this block, and
              not a set of fields, is what closes the gap this note used to
              describe. */}
          {templates.length ? (
            <div className="ae-tpl">
              <label htmlFor="ae-tpl-pick"><b>Apply one of your templates</b></label>
              <div className="ae-tpl-row">
                <select
                  id="ae-tpl-pick"
                  value={tplId || templates[0]?.id || ''}
                  onChange={(e) => setTplId(e.target.value)}
                >
                  {templates.map((t) => (
                    <option key={t.id} value={t.id}>
                      {t.label}{t.summary ? ` — ${t.summary}` : ''} (saved {templateAge(t.savedAt, `${today ?? ''}T12:00:00.000Z`)})
                    </option>
                  ))}
                </select>
                <button type="button" className="btn" onClick={onApplyTemplate} disabled={busy}>
                  Apply
                </button>
              </div>
              <div className="muted ae-tpl-hint">
                Fills the rule fields below and the account size from the template — including
                the four this form has no boxes for: whether your drawdown floor <i>freezes</i>
                {' '}once you are up and by how much, how hard the daily limit is enforced, and
                whether the consistency rule applies to evaluations or funded accounts.
                {' '}<b>Nothing is written until you press Save</b>, and your payout count is
                never touched.
              </div>
              {applied ? <div className="green ae-tpl-applied" role="status">{applied}</div> : null}
            </div>
          ) : (
            // ⚠ The old note, kept for the case it is still TRUE. With no saved
            // templates there is genuinely no way to set these four here, and
            // saying nothing would leave a trader believing the form shows every
            // rule the account has.
            <div className="ae-note">
              <b>Four rules are not editable here, and are kept exactly as they are.</b>
              {' '}Whether your drawdown floor <i>freezes</i> once you are up and by
              how much, how hard the daily limit is enforced, and whether the
              consistency rule applies to evaluations or funded accounts. Those are
              set by applying a saved template — save one from an account that
              already has the right rules, and an Apply box appears here.
            </div>
          )}

          {/* 🚨 THE ONE BLOCK HERE THAT REACHES PAST THIS ACCOUNT. It is last,
              after everything confined to this row, and it carries its own Save
              — see `CommissionTable`. It reads the firm from the DRAFT, not the
              saved account, so typing a firm and setting its rates works in one
              pass. */}
          <h4 className="ae-sec">Commissions at {draft.firm.trim() || 'this firm'}</h4>
          <CommissionTable firm={draft.firm} />

          <div className="ae-note">
            {/* ⚠ THIS SENTENCE SHRINKS AS THE WORK LANDS, and shrinking it is the
                job — it named the commission table until the table shipped six
                inches above it. Re-read it whenever anything here lands. */}
            <b>Also still in the current client:</b> the adjustments list —
            payouts, eval fees, resets — and the Recompute MAE/MFE button, which
            spends metered market data and overwrites excursion figures on
            hundreds of trades.
          </div>

          {saveError ? <div className="ae-error" role="alert">{saveError}</div> : null}
        </div>

        <footer className="modal-foot">
          <button type="button" className="btn" onClick={onCancel} disabled={busy}>Cancel</button>
          {/* ⚠ NOT `primary` UNTIL THERE IS SOMETHING TO SAVE. A brand-orange
              button that cannot be pressed reads as a broken Save rather than
              as "nothing has changed yet". */}
          <button type="button" className={`btn${dirty ? ' primary' : ''}`}
            onClick={() => void onSave()} disabled={!canSave || !dirty}>
            {busy ? 'Saving…' : dirty ? 'Save' : 'No changes'}
          </button>
        </footer>
      </div>
    </div>,
    document.body,
  );
}

// ---------------------------------------------------------------------------

function Field(
  { label, hint, error, full, children }: {
    label: string; hint?: string; error?: string | undefined;
    full?: boolean; children: ReactNode;
  },
) {
  return (
    <label className={`ae-field${full ? ' full' : ''}${error ? ' bad' : ''}`}>
      <span className="ae-label">
        {label}{hint ? <i className="ae-hint"> ({hint})</i> : null}
      </span>
      {children}
      {error ? <span className="ae-msg">{error}</span> : null}
    </label>
  );
}

/**
 * ⚠ ONE `<label>` AROUND THE WHOLE THING, heading included.
 *
 * The first version put the heading in a bare `<span>` outside a `<label>` that
 * wrapped only the checkbox and its note — so the control's accessible name was
 * "start + trade P&L + adjustments", with nothing tying it to "Auto-calc
 * balance". A screen reader announces the note and never the field. Caught by a
 * test that could not find the checkbox by the name printed above it, which is
 * the same question a person using one would be asking.
 */
function Check(
  { label, note, checked, onChange }: {
    label: string; note: string; checked: boolean; onChange: (v: boolean) => void;
  },
) {
  return (
    <label className="ae-field">
      <span className="ae-label">{label}</span>
      <span className="ae-check">
        <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} />
        <span className="muted">{note}</span>
      </span>
    </label>
  );
}

export type { DraftErrors };
